import { useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Alert from '@material-ui/lab/Alert';
import { useAsync } from 'react-use';
import { FieldValidation } from '@rjsf/utils';
import { OneTrustAssessmentServiceApiRef } from '../../services/OneTrustAssessment.service';

export class NotApprovedError extends Error {
  constructor(appShortname: string) {
    super(`Application ${appShortname} has not completed OneTrust assessment`);
    this.name = 'NotApprovedError';
  }
}

export interface OneTrustValidationData {
  isApproved: boolean;
  appShortname: string;
}

type ScaffolderFormData = {
  cmdbAppShortName: { shortName: string };
} & Record<string, unknown>;

interface ScaffolderFormContext {
  formData?: ScaffolderFormData;
}

export const AppShortnameOneTrustAssessmentValidator = ({
  onChange,
  formContext,
}: FieldExtensionComponentProps<OneTrustValidationData | null>) => {
  const oneTrustAssessmentService = useApi(OneTrustAssessmentServiceApiRef);
  const typedFormContext = formContext as ScaffolderFormContext | undefined;
  const appShortname = typedFormContext?.formData?.cmdbAppShortName?.shortName ?? '';

  const { loading, error, value } = useAsync(async () => {
    const isApproved = await oneTrustAssessmentService.isAppShortNameApproved(appShortname);

    if (!isApproved) {
      throw new NotApprovedError(appShortname);
    }

    return {
      isApproved: true,
      appShortname,
    };
  }, [appShortname, oneTrustAssessmentService]);

  useEffect(() => {
    if (loading) {
      return;
    }

    let nextValue: OneTrustValidationData | null;
    if (error || value === undefined) {
      nextValue = null;
    } else {
      nextValue = value;
    }

    onChange(nextValue);
  }, [loading, error, value, onChange, appShortname]);

  if (!appShortname) {
    return null;
  }

  return (
    <Grid container spacing={2} style={{ marginTop: '16px', marginBottom: '16px' }}>
      <Grid item xs={12}>
        {loading && (
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <CircularProgress size={20} aria-label={`Loading OneTrust validation status for ${appShortname}`} />
            </Grid>
            <Grid item>
              <Typography>Checking if {appShortname} has completed OneTrust assessment...</Typography>
            </Grid>
          </Grid>
        )}

        {!loading && error && error instanceof NotApprovedError && (
          <Alert severity="error">
            OneTrust Assessment Required: {appShortname} has not completed the OneTrust assessment. Please complete the
            GenAI onboarding process first. Follow this documentation:{' '}
            <Link
              href="https://genai.aa.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
            >
              GenAI Onboarding Process
            </Link>
            . If you have any questions, reach out to{' '}
            <Link
              href="mailto:DL_GaaS@aa.com"
              style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
            >
              DL_GaaS@aa.com
            </Link>
            .
          </Alert>
        )}
        {!loading && error && !(error instanceof NotApprovedError) && (
          <Alert severity="error">
            {error.message?.includes('401') ||
            error.message?.includes('403') ||
            (error as any).response?.status === 401 ||
            (error as any).response?.status === 403 ? (
              <>
                Authorization Failed: Unable to authenticate with OneTrust API for {appShortname}. The API token may be
                expired or invalid. Please contact{' '}
                <Link
                  href="mailto:DL_GaaS@aa.com"
                  style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
                >
                  DL_GaaS@aa.com
                </Link>{' '}
                to request a token refresh.
              </>
            ) : (
              <>
                Unable to Verify OneTrust Status: We encountered an issue while checking the OneTrust assessment status
                for {appShortname}. This may be due to a temporary service issue or invalid data. Please try again
                later, or contact{' '}
                <Link
                  href="mailto:DL_GaaS@aa.com"
                  style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
                >
                  DL_GaaS@aa.com
                </Link>{' '}
                for assistance.
              </>
            )}
          </Alert>
        )}
      </Grid>
    </Grid>
  );
};

export const appShortnameOneTrustAssessmentValidatorValidation = (
  value: OneTrustValidationData | null,
  validation: FieldValidation,
) => {
  if (!value) {
    validation.addError('Unable to verify OneTrust assessment status. Please try again or contact DL_GaaS@aa.com');
  } else if (!value.isApproved) {
    validation.addError('Application must complete OneTrust assessment before proceeding');
  }
};
